import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format date like "Sat 9th September"
const formatDateString = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // Return original if invalid
  
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const month = months[date.getMonth()];
  
  // Add ordinal suffix
  const suffix = (dayNum === 1 || dayNum === 21 || dayNum === 31) ? "st" 
    : (dayNum === 2 || dayNum === 22) ? "nd" 
    : (dayNum === 3 || dayNum === 23) ? "rd" 
    : "th";
  
  return `${dayName} ${dayNum}${suffix} ${month}`;
};

// Parse and reformat message with embedded dates
const formatTickerMessage = (message) => {
  // Match date pattern YYYY-MM-DD and location pattern "in Location!"
  const dateMatch = message.match(/(\d{4}-\d{2}-\d{2})/);
  const locationMatch = message.match(/in ([^!]+)!/);
  
  if (dateMatch) {
    const formattedDate = formatDateString(dateMatch[1]);
    let newMessage = message.replace(dateMatch[1], formattedDate);
    
    // Reformat to "Sat 9th September - Croydon: Event Name"
    if (locationMatch) {
      const location = locationMatch[1].trim();
      // Extract event name (text before the date)
      const eventName = message.split(' - ')[0].replace(/^[^\s]+\s/, ''); // Remove emoji prefix
      newMessage = `${formattedDate} - ${location}: ${eventName}`;
    }
    
    return newMessage;
  }
  
  return message;
};

const EventsTicker = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${API}/ticker/messages`);
        setMessages(response.data.messages || []);
      } catch (error) {
        // Silent fail
      }
    };
    fetchMessages();
  }, []);

  if (messages.length === 0) return null;

  // Duplicate messages for seamless loop
  const allMessages = [...messages, ...messages];

  return (
    <div className="bg-gradient-to-r from-purple-200 via-pink-200 to-orange-200 overflow-hidden py-2">
      <div className="ticker-wrapper">
        <div className="ticker-content animate-ticker">
          {allMessages.map((msg, idx) => {
            const displayMessage = formatTickerMessage(msg.message);
            
            return (
              <span key={idx} className="ticker-item inline-flex items-center mx-8 text-purple-800 font-medium whitespace-nowrap">
                <span className="mr-2 text-lg">{msg.emoji || "🌟"}</span>
                {msg.link ? (
                  <a 
                    href={msg.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {displayMessage}
                  </a>
                ) : (
                  <span>{displayMessage}</span>
                )}
                <span className="mx-8 text-purple-400">•</span>
              </span>
            );
          })}
        </div>
      </div>
      <style>{`
        .ticker-wrapper {
          display: flex;
          width: 100%;
          overflow: hidden;
        }
        .ticker-content {
          display: flex;
          animation: ticker 30s linear infinite;
        }
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .ticker-content:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default EventsTicker;
